import lodash from 'lodash'
import * as sentry from '../../src/utils/sentry'
import { strictParseInt } from '../../../shared/lib/number.no-deps'
import type { Cust_ICustomer } from '../Cust/Cust.ICustomer'
import { DS_setData } from '../DS/DS.Data'
import { LS_accessTokenRemove, LS_accessTokenSet } from '../LS/LS.accessToken'
import { LS_customerRemove } from '../LS/LS.customer'
import { Rout_toSettings } from '../Rout/Rout.settings'
import { RelayState } from '../../../shared/domain/auth'
import { Rout_construct, Rout_getPath, Rout_replace } from '../Rout/Rout'

type Signature = {
    relayState: RelayState
    signature: string
    success: boolean
    signedAtTime: Date
    reportStatusId?: string
}

type TUrlParams =
    | {
          relayState: 'password-change'
          token: string
          success: 'true'
      }
    | {
          relayState: 'password-change'
          success: 'false'
      }
    | {
          relayState: 'login'
          token: string
          customerId: string
          customerName: string
          customerSlug: string
          serverURL: string
      }
    | {
          relayState: 'cancelled-login'
      }
    | Signature

export async function Auth_redirectReceived(url: string) {
    // Split on ?, & and =
    const pairs = url
        .split('?')[1]
        .split('&')
        .map((s) => s.split('=').map(decodeURIComponent))

    // Create a dictionary for key value pairs
    const params = lodash.fromPairs(pairs) as TUrlParams

    if (Object.values(RelayState).includes(params.relayState as any)) {
        const { relayState, signature, success, signedAtTime, reportStatusId } = params as Signature
        try {
            Rout_replace(
                Rout_construct(Rout_getPath(), { relayState, signature, success, signedAtTime, reportStatusId })
            )
        } catch (error) {
            sentry.err(error, {})
        }
        return
    }
    // Wipe the search parameters to allow reloading
    history.pushState({}, '', location.href.replace(location.search, ''))

    // Validate shared params for all relayStates
    if (!params.relayState) throw new Error('Expected to receive relayState from redirect')

    // Password Change
    if (params.relayState === 'password-change') {
        // Validate password change specifc properties
        if (!params.success) throw new Error('Expected to receive success from redirect')

        // If they pressed the back button and did not change their password
        if (params.success !== 'true') {
            // Go back to the settings page
            Rout_toSettings('security')
            return
        }

        // Validate success specific properties
        if (!params.token) throw new Error('Expected to receive token from redirect')

        // Persist new token in localStorage
        LS_accessTokenSet(params.token)

        // Use the params to set the data in the datastore
        DS_setData('PasswordSetRedirectReceived', () => ({
            accessToken: params.token,
        }))

        // Go to the success page
        Rout_toSettings('security-success')
    }

    if (params.relayState === 'login') {
        try {
            // Validate login specifc properties
            if (!params.customerId) throw new Error('Expected to receive customerId from redirect')
            if (!params.customerName) throw new Error('Expected to receive customerName from redirect')
            if (!params.customerSlug) throw new Error('Expected to receive customerSlug from redirect')
            if (!params.serverURL) throw new Error('Expected to receive serverURL from redirect')
            if (!params.token) throw new Error('Expected to receive token from redirect')

            // Extract the customer from the params
            const customer: Cust_ICustomer = {
                id: strictParseInt(params.customerId),
                name: decodeURI(params.customerName),
                domainName: params.serverURL,
                slug: params.customerSlug,
            }

            // Inform sentry to improve error messages
            sentry.customerSet(customer.id, customer.name)

            // Use the params to set the data in the datastore
            DS_setData('LoginRedirectReceived', () => ({
                accessToken: params.token,
                customer,

                // In dev, give the developer the choice to login in locally
                auth: { status: ENV.isProduction || ENV.isTest ? 'pending-sync' : 'dev-select' },
            }))
        } catch (error) {
            sentry.err(error, {})

            // Explain that the login failed and that they should try again
            // Also clear out the cutomer and token
            DS_setData('AuthRedirectFailed', () => ({
                auth: { status: 'failure' },
                accessToken: null,
                customer: null,
            }))

            // Remove the customer and token from localStorage
            LS_customerRemove()
            LS_accessTokenRemove()
        }
    }
}
